import React from 'react';
import { Link } from 'react-router-dom';
import './HomePage.css';

const symptoms = [
  { num: 'i',   title: 'Longer hours, more people', body: 'Yet output stays flat.' },
  { num: 'ii',  title: 'Constant firefighting',     body: 'Reacting, never building.' },
  { num: 'iii', title: 'Margins under pressure',    body: 'Costs rise, returns shrink.' },
];

const metrics = [
  { value: '≈99%', label: 'On-time-in-full',          from: 'up from 75–88%, in four months' },
  { value: '96%',  label: 'Monthly waste reduction',  from: 'R4M → R150K, spillage and write-offs' },
  { value: '98%',  label: 'Monthly overtime savings', from: 'R700K → R15K, catch-up shifts eliminated' },
];

const beliefs = [
  {
    title: 'Your team runs it after we leave.',
    body: 'Engagements end when your people operate independently. We measure success by whether you still need us, then work until you don’t.',
  },
  {
    title: 'We fix the one thing limiting everything else.',
    body: 'Optimising departments in isolation rarely moves the whole business forward, and often makes it worse. We work at the system level.',
  },
  {
    title: 'Solutions built inside your culture so they stick.',
    body: 'We embed alongside your team. Solutions are designed through your people, in your language. That is the only reason they last.',
  },
];

const processSteps = [
  { numeral: 'I',   title: 'Immerse',            body: 'Live the business with your leaders. Your goals, your numbers, your lived reality.', artifact: 'Shared reality' },
  { numeral: 'II',  title: 'Locate & design',    body: 'Find what is holding the business back. Design the fix with your team.',              artifact: 'Named constraint' },
  { numeral: 'III', title: 'Implement together', body: 'Hands-on alongside your team. We train by doing the work together.',                  artifact: 'Working fix' },
  { numeral: 'IV',  title: 'Verify',             body: 'Measure against real performance. No results, no fees.',                              artifact: 'Proof in numbers' },
  { numeral: 'V',   title: 'Step away',          body: 'Exit when your team can find the next constraint without us.',                        artifact: 'Self-sustaining team' },
];

const HomePage = () => {
  return (
    <div className="homepage">
      {/* ===== HERO ===== */}
      <section className="hero">
        <div className="hero__lines"></div>
        <div className="hero__stars"></div>
        <div className="frame">
          <div className="hero__inner">
            <div className="hero__mark">
              <img src="/img/lh_logo-white-1200x387.webp" alt="RussellStone Lighthouse mark" />
            </div>
            <div className="hero__copy">
              <div className="eyebrow-rule">Est. 2024 · Pretoria</div>
              <div className="hero__wordmark">RussellStone Lighthouse</div>
              <h1 className="hero__title">From Firefighting to <em>Performance</em></h1>
              <p className="hero__subcap">Honest counsel for businesses stuck running their own systems.</p>
              <p className="hero__lede">
                What baffles us most is the company that fails silently, then suddenly. Almost never betrayal or black swans or a bad idea. Almost always a system neglected for too long. We learned to see that inside our own group, before we offered the approach externally.
              </p>
              <div className="hero__cta">
                <Link to="/contact"  className="btn btn--primary">Start the conversation <span className="arrow"></span></Link>
                <Link to="/approach" className="btn btn--ghost">How we work</Link>
              </div>
            </div>
          </div>
        </div>
        <div className="hero__horizon" aria-hidden="true"></div>
      </section>

      {/* ===== SYMPTOMS ===== */}
      <section className="section band--paper stitch--top">
        <div className="frame">
          <div className="sidehead">
            <aside className="sidehead__label">
              <div className="sidehead__roman">I</div>
              <div className="sidehead__cat">You might be here because</div>
            </aside>
            <div>
              <h2 className="sidehead__title">
                The patterns are <em>consistent</em>.
              </h2>
              <p className="sidehead__lede">
                Three signals show up first. They look like effort problems and they almost never are. They persist not because people aren’t capable, but because the system hasn’t been addressed.
              </p>

              <div className="symptoms">
                {symptoms.map((s) => (
                  <article key={s.num} className="symptom">
                    <div className="symptom__num">{s.num}</div>
                    <h3 className="symptom__title">{s.title}</h3>
                    <p className="symptom__body">{s.body}</p>
                  </article>
                ))}
              </div>

              <p className="symptoms-note">
                If two or three of these are true at once, you are usually looking at one underlying constraint. The three are symptoms of it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== METRICS BAND ===== */}
      <section className="metrics-band">
        <div className="metrics-band__bg"></div>
        <div className="frame">
          <div className="eyebrow-rule">II · Proof, on our own group</div>
          <h2 className="metrics-band__title">
            RussellStone Foods, before we worked with anyone else.
          </h2>
          <p className="metrics-band__attrib">An 18-month engagement inside our own group.</p>

          <div className="metric-row">
            {metrics.map((m) => (
              <div key={m.label} className="metric--sectional">
                <div className="big">{m.value}</div>
                <div className="lbl">{m.label}</div>
                <div className="from">{m.from}</div>
              </div>
            ))}
          </div>

          <p className="metrics-band__footnote">
            Also delivered: 40% electricity reduction while capacity held or grew on individual lines, and R2.5–R4M of machinery purchases deferred. Flow management showed those machines were only productive about 35% of the time. They weren’t undersized. They were choked.
          </p>
        </div>
      </section>

      {/* ===== BELIEFS ===== */}
      <section className="section band--paper stitch--top">
        <div className="frame">
          <div className="sidehead">
            <aside className="sidehead__label">
              <div className="sidehead__roman">III</div>
              <div className="sidehead__cat">What we build for</div>
            </aside>
            <div>
              <h2 className="sidehead__title">Three outcomes every engagement has to deliver.</h2>
              <p className="sidehead__lede">
                If we cannot deliver these three, we are not the right firm. They sit underneath every decision we make in the room.
              </p>

              <div className="beliefs">
                {beliefs.map((b, i) => (
                  <article key={b.title} className="belief-column">
                    <div className="belief-column__num">{String(i + 1).padStart(2, '0')}</div>
                    <h3 className="belief-column__title">{b.title}</h3>
                    <p className="belief-column__body">{b.body}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIAL ===== */}
      <section className="testimonial-section">
        <div className="testimonial-section__wave" aria-hidden="true"></div>
        <div className="stage">
          <div className="testimonial-section__co">Rotondo Walnuts</div>
          <blockquote className="testimonial-section__quote">
            You pulled me out of the weeds. Five in, two out, there’s three missing, where are they. It wasn’t about the detail. You looked at the process as a whole and worked out where the bottlenecks were.
          </blockquote>
          <div className="testimonial-section__attrib">
            <span className="testimonial-section__name">Adam Prowse</span>
            <span className="sep">·</span>
            <span className="testimonial-section__role">COO &amp; Co-Owner, Rotondo Walnuts</span>
          </div>
        </div>
      </section>

      {/* ===== PROCESS ===== */}
      <section className="section band--paper stitch--top">
        <div className="frame">
          <div className="sidehead sidehead--sticky home-process__head">
            <aside className="sidehead__label">
              <div className="sidehead__roman">IV</div>
              <div className="sidehead__cat">The engagement</div>
            </aside>
            <div>
              <h2 className="sidehead__title">Five stages. In order.</h2>
              <p className="sidehead__lede">
                Each movement protects the next from the most common failure mode of the one before it. Your business continues operating throughout.
              </p>
            </div>
          </div>

          <div className="process--sectional">
            {processSteps.map((s) => (
              <div key={s.numeral} className="step">
                <div className="step__pip">{s.numeral}</div>
                <h4 className="step__title">{s.title}</h4>
                <p className="step__body">{s.body}</p>
                <span className="step__artifact">{s.artifact}</span>
              </div>
            ))}
          </div>

          <div className="process-note">
            <p>
              Engagements typically run 3–18 months. Pace is set by the business, not the consultant. Accelerated work calls for closer collaboration; the steps stay the same.
            </p>
            <Link to="/approach" className="btn btn--outline-paper">
              See the approach <span className="arrow"></span>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="final-cta" id="contact">
        <div className="final-cta__lines"></div>
        <div className="stage">
          <div className="eyebrow-rule">V · Start</div>
          <h2 className="final-cta__title">Start with a conversation.</h2>
          <p className="final-cta__lede">
            We respond within 24 hours with an honest assessment of whether we can help. If our approach doesn’t deliver measurable results, we don’t earn fees for them.
          </p>
          <div className="final-cta__row">
            <Link to="/contact" className="btn btn--primary">
              Start a conversation <span className="arrow"></span>
            </Link>
            <a href="mailto:contact@rslighthouse.com" className="btn btn--ghost">
              contact@rslighthouse.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
